/**
 * Analytics dashboard API — the drill-down overview surface.
 * GET /api/analytics/overview is the single L0 round trip; drill views reuse
 * existing endpoints (debt-aging, debt-report, invoices, scanners).
 */
import { api } from './client';
import type { DebtAgingBucketDto, DebtAgingPersonDto } from './reports';

export interface AnalyticsReceivablesDto {
  totalOutstanding: number;
  overdueTotal: number;
  overdueSharePercent: number;
  personCount: number;
  buckets: DebtAgingBucketDto[];
  topDebtors: DebtAgingPersonDto[];
}

export interface AnalyticsPayablesDto {
  totalOutstanding: number;
  personCount: number;
  buckets: DebtAgingBucketDto[];
}

export interface CashAccountBalanceDto {
  accountNumber: number;
  accountName: string;
  balance: number;
}

export interface AnalyticsCashDto {
  total: number;
  source: 'bankAccounts' | 'heuristic271' | 'none';
  accounts: CashAccountBalanceDto[];
}

export interface AnalyticsVatDto {
  periodFrom: string;
  periodTo: string;
  salesVat: number;
  purchaseVat: number;
  netVatPayable: number;
}

export interface AnalyticsProblemEntryDto {
  id: string;
  timestamp: string;
  scanner: string;
  title: string;
  /** "Critical" | "Warning" | "Info" | "Error"; empty for pre-severity rows. */
  severity: string;
  /** Raw scanner evidence JSON — contains drill ids (personId, invoiceId, journalEntryId). */
  evidence: string;
  confidence: number;
}

export interface AnalyticsProblemGroupDto {
  scanner: string;
  count: number;
  latestTimestamp: string | null;
  latestTitle: string | null;
  latestSeverity: string | null;
}

export interface AnalyticsProblemsDto {
  lastRunUtc: string | null;
  totalFindings: number;
  groups: AnalyticsProblemGroupDto[];
  latest: AnalyticsProblemEntryDto[];
}

export interface AnalyticsFactDto {
  key: string;
  value: number;
  format: 'money' | 'percent' | 'count';
  targetView: string | null;
}

export interface AttentionInboxItemDto {
  category: 'Deadline' | 'OverdueInvoice' | 'UnmatchedPayment' | 'PayrollAccrual' | 'Finding';
  title: string;
  severity: string | null;
  timestamp: string;
  amount: number | null;
  dueDate: string | null;
  daysOverdue: number | null;
  drillKind: 'Invoice' | 'Journal' | 'Customer' | 'Findings';
  invoiceId: number | null;
  journalEntryId: number | null;
  personId: number | null;
  automated: boolean;
  agentName: string;
  description: string | null;
  suggestedAction: string | null;
  stableKey: string | null;
  routineId: number | null;
  routineKind: string | null;
  isPremade: boolean;
  alarmCode: string | null;
  missedPeriods: string[] | null;
  catchupDrafts: { period: string; journalEntryId: number; amount: number }[] | null;
  /** Human labels for the KUR chips — names/numbers, never bare ids. */
  personName: string | null;
  documentNumber: string | null;
  /** Raw evidence JSON when the agent surfaces it (e.g. CopilotInsight cards). */
  evidence?: string | null;
}

export interface AttentionInboxDto {
  generatedAtUtc: string | null;
  totalItems: number;
  categoryCounts: { category: string; count: number }[];
  items: AttentionInboxItemDto[];
  /** Info-tier notices — real, but not work (the collapsed "Stebėjimai" section). */
  observations: AttentionInboxItemDto[];
}

export interface AnalyticsOverviewDto {
  asOfDate: string;
  receivables: AnalyticsReceivablesDto;
  payables: AnalyticsPayablesDto;
  cash: AnalyticsCashDto;
  vat: AnalyticsVatDto;
  problems: AnalyticsProblemsDto;
  facts: AnalyticsFactDto[];
}

export interface DebtReportDto {
  person: {
    id: number;
    name: string;
    code: string;
    receivablesAccount: number;
    payablesAccount: number;
  };
  startBalance: number;
  debitTurnover: number;
  creditTurnover: number;
  endBalance: number;
}

export interface DecisionLogEntryDto {
  id: string;
  timestamp: string;
  agentName: string;
  authorityLevel: string;
  inputs: string;
  policyVersion: string;
  confidenceScore: number;
  evidence: string;
  outcome: string;
  severity: string;
  isReversible: boolean;
  wasReverted: boolean;
  reviewerId: string | null;
  reviewedAt: string | null;
  tenantId: string;
}

export interface PaymentMatchDto {
  journalEntryId: number;
  documentNumber: string;
  date: string;
  amount: number;
  personId: number | null;
  personName: string | null;
  content: string | null;
  amountDifference: number;
}

export interface AiStatusDto {
  aiEnabled: boolean;
  workspaceId: string;
  lastSyncedAtUtc: string | null;
}

/** Extract known drill ids from a scanner finding's evidence JSON (defensive).
 * `entryId` (UnusualPostings) is an alias of journalEntryId. id1/id2 are
 * deliberately skipped — invoices in DuplicateInvoices, entries in
 * DuplicatePayments (ambiguous). */
export function parseEvidenceIds(evidence: string): { personId?: number; invoiceId?: number; journalEntryId?: number; journalEntryIds?: number[] } {
  try {
    const raw = JSON.parse(evidence) as Record<string, unknown>;
    const num = (v: unknown): number | undefined =>
      typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    const numList = (v: unknown): number[] | undefined =>
      Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x)) : undefined;
    const ids = numList(raw.entryIds);
    return {
      personId: num(raw.personId),
      invoiceId: num(raw.invoiceId),
      journalEntryId: num(raw.journalEntryId) ?? num(raw.entryId) ?? ids?.[0],
      journalEntryIds: ids,
    };
  } catch {
    return {};
  }
}

export const analyticsApi = {
  overview: () =>
    api.get<AnalyticsOverviewDto>('/analytics/overview'),

  inbox: () =>
    api.get<AttentionInboxDto>('/analytics/inbox'),

  /** The accounting journey: one row per fiscal year — closed (✓) or not. */
  fiscalYears: () =>
    api.get<{ year: number; isCurrent: boolean; closed: boolean; closedAt: string | null }[]>(
      '/analytics/fiscal-years'),

  runScanners: () =>
    api.post<{ results: unknown[] }>('/scanners/execute-all'),

  /** Match a bank payment to an invoice (writes a payment_link only; source "a5suite"). */
  autoSettle: (invoiceJournalEntryId: number, bankJournalEntryId: number) =>
    api.post<{ paymentLinkId: number; settleAmount: number; alreadyLinked: boolean }>(
      '/scanners/auto-settle',
      { invoiceJournalEntryId, bankJournalEntryId },
    ),

  /** Create a per-person auto-match rule (model C: learn from user). */
  createMatchRule: (personId: number, amountTolerance?: number) =>
    api.post('/analytics/match-rules', { personId, amountTolerance }),

  listMatchRules: () =>
    api.get<{ id: number; personId: number; amountTolerance: number; autoEnabled: boolean; timesUsedAuto: number }[]>(
      '/analytics/match-rules',
    ),

  deleteMatchRule: (id: number) =>
    api.delete(`/analytics/match-rules/${id}`),

  debtReport: (personId: number, dateTo: string, type: 'receivable' | 'payable' = 'receivable') =>
    api.get<DebtReportDto>('/reports/debt-report', { personId, dateTo, type }),

  findings: (params: { scanner?: string; take?: number } = {}) =>
    api.get<{ items: DecisionLogEntryDto[]; totalCount: number }>('/scanners/results', {
      skip: 0,
      take: params.take ?? 100,
    }),

  findMatchingPayments: (invoiceJournalEntryId: number) =>
    api.post<PaymentMatchDto[]>('/scanners/find-matching-payments', { invoiceJournalEntryId }),

  aiStatus: (workspaceId: string) =>
    api.get<AiStatusDto>(`/workspaces/${workspaceId}/ai-status`),
};
